
import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useStore } from 'react-redux';
import { useAppSelector } from 'redux-store';
import { useRouter } from 'next/router';
import Image from 'next/image';
import cn from 'classnames';

import Carousel from 'components/complex/Carousel';
import FormattedPrice from 'components/basic/FormattedPrice';

import * as AuthSelectors from 'domains/auth/auth.selectors';
import { PaymentProcessingStateEnum } from 'domains/payments/processing/interfaces.processing';
import * as paymentProcessingSelectors from 'domains/payments/processing/selectors.processing';
import * as productSelectors from 'domains/products/products.selectors';
import type { ProductsTypeV3 } from 'domains/products/products.types';

import type { AppState } from 'redux-store';

import styles from './styles.module.scss';

import { ProcessingPageAbstract } from 'containers/payments/ProcessingPageAbstract';
import type { ProductComponentAbstractProps } from 'containers/payments/ProductsListAbstract';
import { H3 } from 'components/basic/Typography';

const Timer = React.lazy(() => import('components/domains/common/Timer'));
const TimerBetween = React.lazy(
  () => import('components/domains/common/TimerBetween'),
);

export interface ProductMiniProps extends ProductComponentAbstractProps {
  className?: string;
  isSingle?: boolean;
}

export interface ProductsListProps {
  component: React.JSXElementConstructor<ProductMiniProps>;
  className?: string;
  products: ProductsTypeV3[];
  activeProduct: ProductsTypeV3 | undefined;
  portalTarget: React.RefObject<HTMLDivElement>;
  onChange?: (product: ProductsTypeV3) => void;
}

export const ProductsListBlackFriday: React.FC<ProductsListProps> = ({
  component: Component,
  className,
  products,
  activeProduct,
  portalTarget,
  onChange,
}) => {
  const store = useStore();
  const router = useRouter();
  const { userAgent } = useAppSelector(state => state.ui);
  const shortProfileStore = useAppSelector(state => state.shortProfile);

  const signalsQuery = router.query.signals as string;

  const isMobile = userAgent.device.type === 'mobile';

  const isSingleProduct = products.length === 1;

  const productForPayment = useSelector(
    paymentProcessingSelectors.productSelector,
  );
  const isAuthenticated = useSelector(AuthSelectors.isAuthSelector);

  const product = useSelector((state: AppState) =>
    // @ts-ignore
    productSelectors.getProductSelector(state, activeProduct?.id),
  );

  const onClickHandler = useCallback(
    async (product: ProductsTypeV3) => {
      // setProductId(product.id);
      onChange?.(product);
      (await import('domains/payments/processing')).paymentsProcessingService.setProductById({ id: product.id, store });
    },
    [store, onChange],
  );

  useEffect(() => {
    async function fn(): Promise<void> {
      if (!product || productForPayment) {
        return;
      }

      if (isAuthenticated && activeProduct?.id) {
        (
          await import('domains/payments/processing')
        ).paymentsProcessingService.showModal({
          store,
          product,
          state: PaymentProcessingStateEnum.CARD,
        });

        return;
      }

      (
        await import('domains/payments/processing')
      ).paymentsProcessingService.showModal({ store, product });
    }

    fn();
  }, [product, store, isAuthenticated]);

  const productsContent = products.map(item => {
    return (
      <Component
        isSelected={activeProduct?.id === item.id}
        isSingle={isSingleProduct}
        key={item.id}
        product={item}
        onClick={onClickHandler}
      />
    );
  });

  const reviews: {
    author: string,
    avatar?: string,
    title: string,
    text: string,
    stars: number,
  }[] = [
    {
      author: 'by Must have health tool',
      // avatar: '/pagesImages/blackfriday/review_avatar1.png',
      title: 'Excellent tool',
      text: 'I use this app from sun up to sun down to monitor my health. If I’m stressed or overwhelmed, it reminds me to take a breather. If I need more sleep, it tells me. If I’m not moving around enough it pushes me to get a workout in. Overall it just helps me put me and my health first. It’s a must have app for me. I’ve already had it 3 years and can’t live without it!',
      stars: 5,
    },
    {
      author: 'by Sam Thorpe',
      // avatar: '/pagesImages/blackfriday/review_avatar1.png',
      title: 'This app made me cry!',
      text: 'If you want to be seen, understood and cared about - this is the app for you. The analysis and personalized support are not only accurate, but I felt that I was not struggling on my own. This is the first app to properly explain HRV in context and present solutions. Better than any other coaching app, stress app, HRV app. Take care of YOU and get this app!',
      stars: 5,
    },
    {
      author: 'by Jasonl1111',
      // avatar: '/pagesImages/blackfriday/review_avatar1.png',
      title: 'Excellent app with great integrations',
      text: 'I track everything. I have various tracking devices in my household, and two fitness trackers, one on each arm. Welltory is definitely my central hub for monitoring my health and lifestyle. Definitely my favourite health tracking app. Would recommend to anyone who enjoys this kind of stuff!',
      stars: 5,
    },
    {
      author: 'by Melonshd',
      // avatar: '/pagesImages/blackfriday/review_avatar1.png',
      title: 'Perfect recovery tool',
      text: 'The accuracy and guidance that this app has is astounding. It gave me crystal clear guidance about when my health was declining so I got went to the Dr just in time. It was serious and I ended up in hospital for a week and I’ve now got a 4-6 month recovery ahead of me.',
      stars: 5,
    }
  ];

  return (
    <div
      className={cn(styles.paymentBlackFridayContainer, className, {
        [styles.isOnePaymentButton]: !isSingleProduct
      })}
    >
      <div className={styles.content}>
        {productsContent}
        {!isMobile &&
          <div ref={portalTarget} />
        }
      </div>

      {signalsQuery &&
        <div className={styles.productsSignalsWrapper}>
          <div className={styles.productsSignalsText}>See signals before<br/>they become problems</div>
        </div>
      }
      <div className={styles.productStatsWrapper}>
        <div className={styles.productDownloadsWrapper}>
          <div className={styles.branchIcon} />
          <div className={styles.productDownload} dangerouslySetInnerHTML={{ __html: `<div class="${styles.productDownloadNumber}">540k+</div>monthly<br/>actives<br/>members` }} />
          <div className={cn(styles.branchIcon, styles.reverse)} />
        </div>

        <div className={styles.productDownloadsWrapper}>
          <div className={styles.branchIcon} />
          <div className={styles.productDownload} dangerouslySetInnerHTML={{ __html: `<div class="${styles.productDownloadNumber}">290k</div><img src='/pagesImages/blackfriday/app_ratings.svg'"><br/>ratings` }} />
          <div className={cn(styles.branchIcon, styles.reverse)} />
        </div>
      </div>

      <div className={styles.productUserStatsWrapper}>
        <div className={styles.productUsersWrapper}>
          <div className={styles.branchColoredIcon} />
          <div className={styles.productUsers} dangerouslySetInnerHTML={{ __html: `<div class="${styles.productUsersNumber}">17m+</div><div class="${styles.productUsersText}">downloads</div>` }} />
          <div className={cn(styles.branchColoredIcon, styles.reverse)} />
        </div>
        <H3 className={styles.productUsersTrustText}>Trusted and loved by over 16 million people across 130+ countries</H3>
        <div className={styles.productUsersCountries}>
          <Image
            src="/pagesImages/blackfriday/user_countries2x.png"
            width={163}
            height={28}
            unoptimized={true}
          />
        </div>
      </div>

      <div className={styles.productReviewsWrapper}>
        <Carousel
          withMobileProgressBar={false}
          settings={{
            arrows: false,
            centerMode: true,
            slidesToShow: 1,
            centerPadding: '0px',
            // onSwipe: onSwipePaymentSlide
          }}
        >
        {
          reviews.map((review) => {
            return (
              <div className={styles.productReview}>
                <div className={styles.productReviewInfo}>
                  <div className={styles.productReviewUserAuthor}>
                    {review.avatar &&
                      <div className={styles.productReviewUserAvatar}>
                        <Image 
                          src={review.avatar}
                          width={34}
                          height={43}
                          layout="fill"
                          objectFit="cover"
                          lazyBoundary="100px"
                        />
                      </div>
                    }
                    <div className={styles.productReviewUserRatingWrapper}>
                      <div className={styles.productReviewUserRating}>
                        <div className={cn(styles.productReviewUserRatingStarDefault, {[styles.productReviewUserRatingStar]: review.stars >= 1})} />
                        <div className={cn(styles.productReviewUserRatingStarDefault, {[styles.productReviewUserRatingStar]: review.stars >= 2})} />
                        <div className={cn(styles.productReviewUserRatingStarDefault, {[styles.productReviewUserRatingStar]: review.stars >= 3})} />
                        <div className={cn(styles.productReviewUserRatingStarDefault, {[styles.productReviewUserRatingStar]: review.stars >= 4})} />
                        <div className={cn(styles.productReviewUserRatingStarDefault, {[styles.productReviewUserRatingStar]: review.stars >= 5})} />
                      </div>
                      <div className={styles.productReviewUserName}>{review.author}</div>
                    </div>
                  </div>
                  <div className={styles.productReviewStats}>50k+ reviews<br/>App Store</div>
                </div>
                <div className={styles.productReviewTitle}>{review.title}</div>
                <div className={styles.productReviewText}>{review.text}</div>
              </div>
            )
          })
        }
        </Carousel>
      </div>

      <div className={styles.productAppFeauturesWrapper}>
        <H3 className={styles.productAppFeauturesTitle}><span>From stress to sleep</span> — find your balance without juggling 10 apps</H3>
        <div className={styles.productAppFeautures}>
          <Image 
            src="/pagesImages/blackfriday/app_features.webp"
            width={724}
            height={243}
            layout="fill"
            objectFit="cover"
            lazyBoundary="100px"
          />
        </div>
      </div>

      <div className={styles.personalizedApproachWrapper}>
        <Image 
          src="/pagesImages/blackfriday/personalized_approach.webp"
          width={375}
          height={130}
          layout="responsive"
          lazyBoundary="100px"
        />
      </div>

      <div className={styles.productPremiumFeaturesWrapper}>
        <H3 className={styles.productPremiumFeaturesTitle}>Go beyond<br/> basic stats</H3>
        <div className={styles.productPremiumFeaturesText}>Track your stress, energy, sleep, and habits — discover what drains you or helps you recharge</div>
        <div className={styles.productPremiumFeatures}>
          <div className={styles.productPremiumFeaturesPremium}>
            <div className={styles.productPremiumFeaturesPremiumTitle}>Premium</div>
            <div className={styles.productPremiumFeaturesPremiumFeatures}>
              <div className={styles.productPremiumFeaturesPremiumFeature}>
                <div className={styles.productPremiumFeaturesPremiumFeatureTitle}>What drains your energy</div>
                <div className={styles.productPremiumFeaturesPremiumFeatureImage}>
                  <Image
                    src="/pagesImages/blackfriday/app_rating_star.svg"
                    width={22}
                    height={21}
                    objectFit="cover"
                  />
                </div>
              </div>
              <div className={styles.productPremiumFeaturesPremiumFeature}>
                <div className={styles.productPremiumFeaturesPremiumFeatureTitle}>When your stress peaks</div>
                <div className={styles.productPremiumFeaturesPremiumFeatureImage}>
                  <Image
                    src="/pagesImages/blackfriday/app_rating_star.svg"
                    width={22}
                    height={21}
                    objectFit="cover"
                  />
                </div>
              </div>
            </div>
            <div className={styles.productPremiumFeaturesPremiumFeature}>
              <div className={styles.productPremiumFeaturesPremiumFeatureTitle}>What leads to burnout</div>
              <div className={styles.productPremiumFeaturesPremiumFeatureImage}>
                <Image
                  src="/pagesImages/blackfriday/app_rating_star.svg"
                  width={22}
                  height={21}
                  objectFit="cover"
                />
              </div>
            </div>
            <div className={styles.productPremiumFeaturesPremiumFeature}>
              <div className={styles.productPremiumFeaturesPremiumFeatureTitle}>What habits support you</div>
              <div className={styles.productPremiumFeaturesPremiumFeatureImage}>
                <Image
                  src="/pagesImages/blackfriday/app_rating_star.svg"
                  width={22}
                  height={21}
                  objectFit="cover"
                />
              </div>
            </div>
            <div className={styles.productPremiumFeaturesPremiumFeature}>
              <div className={styles.productPremiumFeaturesPremiumFeatureTitle}>What helps you calm down</div>
              <div className={styles.productPremiumFeaturesPremiumFeatureImage}>
                <Image
                  src="/pagesImages/blackfriday/app_rating_star.svg"
                  width={22}
                  height={21}
                  objectFit="cover"
                />
              </div>
            </div>
            <div className={styles.productPremiumFeaturesPremiumFeature}>
              <div className={styles.productPremiumFeaturesPremiumFeatureTitle}>How sleep impacts you</div>
              <div className={styles.productPremiumFeaturesPremiumFeatureImage}>
                <Image
                  src="/pagesImages/blackfriday/app_rating_star.svg"
                  width={22}
                  height={21}
                  objectFit="cover"
                />
              </div>
            </div>
          </div>
          <div className={styles.productPremiumFeaturesFree}>
            <div className={styles.productPremiumFeaturesFreeTitle}>Free</div>
              <div className={styles.productPremiumFeaturesPremiumFeature}>
                <div className={styles.productPremiumFeaturesPremiumFeatureImage}>
                  <Image
                    src="/pagesImages/blackfriday/app_rating_star.svg"
                    width={22}
                    height={21}
                    objectFit="cover"
                  />
                </div>
              </div>
              <div className={styles.productPremiumFeaturesPremiumFeature}>
                <div className={styles.productPremiumFeaturesPremiumFeatureImage}>
                  <Image
                    src="/pagesImages/blackfriday/app_rating_star-default.svg"
                    width={22}
                    height={21}
                    objectFit="cover"
                  />
                </div>
              </div>
              <div className={styles.productPremiumFeaturesPremiumFeature}>
                <div className={styles.productPremiumFeaturesPremiumFeatureImage}>
                  <Image
                    src="/pagesImages/blackfriday/app_rating_star-default.svg"
                    width={22}
                    height={21}
                    objectFit="cover"
                  />
                </div>
              </div>
              <div className={styles.productPremiumFeaturesPremiumFeature}>
                <div className={styles.productPremiumFeaturesPremiumFeatureImage}>
                  <Image
                    src="/pagesImages/blackfriday/app_rating_star.svg"
                    width={22}
                    height={21}
                    objectFit="cover"
                  />
                </div>
              </div>
              <div className={styles.productPremiumFeaturesPremiumFeature}>
                <div className={styles.productPremiumFeaturesPremiumFeatureImage}>
                  <Image
                    src="/pagesImages/blackfriday/app_rating_star-default.svg"
                    width={22}
                    height={21}
                    objectFit="cover"
                  />
                </div>
              </div>
              <div className={styles.productPremiumFeaturesPremiumFeature}>
                <div className={styles.productPremiumFeaturesPremiumFeatureImage}>
                  <Image
                    src="/pagesImages/blackfriday/app_rating_star.svg"
                    width={22}
                    height={21}
                    objectFit="cover"
                  />
                </div>
              </div>
          </div>
        </div>
      </div>

      <div className={styles.productUnlockPerfectHealthWrapper}>
        <H3 className={styles.productUnlockPerfectHealthTitle}>Discover a new way<br/> to unlock your<br/> perfect health</H3>
        <div className={styles.productUnlockPerfectHealthImage}>
          <Image 
            src="/pagesImages/blackfriday/unlock_perfect_health.webp"
            width={277}
            height={118}
            layout="responsive"
            objectFit="contain"
            unoptimized={true}
            lazyBoundary="100px"
          />
        </div>
        {product?.prices[0].interval === 'year' &&
          <>
            <div className={styles.productUnlockPerfectHealthPriceWeek}>
              Just
              {' '}
              <span>
                <FormattedPrice
                  price={(+(product.prices[0].discount_price ?? product.prices[0].price) / 52).toFixed(2)}
                  currency={product.prices[0].currency}
                />
                /week
              </span>
            </div>
            <div className={styles.productUnlockPerfectHealthPrice}>
              One year for only
              {' '}
              <FormattedPrice
                price={(+(product.prices[0].discount_price ?? product.prices[0].price))}
                currency={product.prices[0].currency}
              />
            </div>
          </>
        }
      </div>

      <div className={styles.productDidYouKnowWrapper}>
        <H3 className={styles.productDidYouKnowTitle}>Did you know?</H3>
        <div
          className={styles.productDidYouKnowDescription}
          dangerouslySetInnerHTML={{ __html: 'Our users <span>are up to twice as likely</span> to catch stress early, pause when it matters most, <br/>and recover faster'}}
        />
        <div className={styles.productDidYouKnowImage}>
          <Image 
            src="/pagesImages/blackfriday/you_know-d.png"
            width={312}
            height={247}
            layout="responsive"
            lazyBoundary="100px"
          />
        </div>
      </div>
    </div>
  );
};
